import { useState, type FormEvent } from 'react';
import { useSeo } from '@/hooks/useSeo';
import PageBanner from '@/components/base/PageBanner';
import Reveal from '@/components/base/Reveal';

const FORM_ADDR = 'https://readdy.ai/api/form/db6a26bghk82orf0cjp0';

const TEAM = [
  { name: '资讯编辑组', role: '版本与活动情报', desc: '追踪版本前瞻、官方公告与活动开启时间，保证信息及时准确。', icon: 'ri-newspaper-line' },
  { name: '角色研究组', role: '角色攻略与数值', desc: '负责角色培养、武器圣遗物与配队思路的实测与整理。', icon: 'ri-user-star-line' },
  { name: '战斗攻略组', role: '深渊与实战教学', desc: '专注深境螺旋、周本与高难挑战的通关思路与手法拆解。', icon: 'ri-sword-line' },
  { name: '社区运营组', role: '内容策划与互动', desc: '策划专栏选题、社区活动与内容更新节奏，维护阅读体验。', icon: 'ri-community-line' },
];

export default function About() {
  useSeo({
    title: '关于我们 – 原神官方游戏站',
    description: '了解原神资讯站的网站使命、内容团队与联系方式，查看免责声明与隐私说明。',
    keywords: '关于原神资讯站,联系我们,免责声明,隐私说明',
    canonicalPath: '/about',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt') return;
      if (typeof value === 'string' && value.trim() !== '') params.append(key, value);
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;

      if (!response.ok || code !== 'OK' || /spam/i.test(String(serverMsg))) {
        setStatus('error');
        setFormError(String(serverMsg) || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试。');
    }
  };

  return (
    <div>
      <PageBanner
        kicker="ABOUT US"
        title="关于原神资讯站"
        subtitle="我们是一群热爱提瓦特的玩家与内容创作者，致力于打造专业、全面且易用的原神资讯与攻略平台。"
        crumbs={[{ label: '首页', to: '/' }, { label: '关于我们' }]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <Reveal>
          <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <a href="#mission">我们的使命</a>
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  原神资讯站以“让每一位旅行者都能轻松获取有用信息”为目标，把版本动态、活动情报、角色攻略与机制解析整理成清晰易读的内容。
                </p>
                <p>
                  我们坚持结构化的内容组织方式：列表页方便筛选，详情页注重可读性，攻略内容强调可执行性。无论是刚入坑的新玩家，还是追求深渊满星的老玩家，都能在这里找到需要的信息。
                </p>
                <p>
                  本站为演示性质的内容平台，所有资讯、角色与活动数据均为虚构示例，用于展示信息架构与交互方式，不代表任何真实官方公告。
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                <a href="#quick-facts">平台速览</a>
              </h3>
              <ul className="mt-4 space-y-3.5 text-sm">
                {[
                  { icon: 'ri-newspaper-line', label: '资讯更新', value: '每日追踪' },
                  { icon: 'ri-user-star-line', label: '角色攻略', value: '全元素覆盖' },
                  { icon: 'ri-book-open-line', label: '深度攻略', value: '机制与实战' },
                  { icon: 'ri-calendar-event-line', label: '活动专题', value: '奖励与时间' },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <span className="flex-1 text-foreground-600">{item.label}</span>
                    <span className="font-label text-foreground-900">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        <Reveal delay={80}>
          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <a href="#team">团队简介</a>
            </h2>
            <p className="mt-3 max-w-3xl text-sm text-foreground-600 md:text-base">
              以下是本站内容团队的职能分工（演示文本），我们以小组协作的方式持续产出内容。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {TEAM.map((member, index) => (
                <div key={member.name} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-background-50">
                    <i className={`${member.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                  <p className="mt-0.5 text-xs font-label text-accent-700">{member.role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
                  <p className="mt-4 text-[11px] text-foreground-400">第 {index + 1} 小组</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        <Reveal delay={120}>
          <section id="contact" className="mt-16 scroll-mt-24">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <a href="#contact">联系方式</a>
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  如果你有内容建议、纠错反馈或合作意向，欢迎通过表单留言，我们会尽快回复。
                </p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span>
                      <span className="block font-label text-foreground-900">邮箱</span>
                      <span className="text-foreground-600">contact@genshin-info-demo.com</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <span>
                      <span className="block font-label text-foreground-900">地址</span>
                      <span className="text-foreground-600">提瓦特大陆 · 蒙德城 · 冒险家协会资讯处</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-time-line"></i>
                    </span>
                    <span>
                      <span className="block font-label text-foreground-900">回复时间</span>
                      <span className="text-foreground-600">工作日 09:00 – 18:00</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-3">
                <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                  {status === 'success' ? (
                    <div className="flex flex-col items-center py-10 text-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                        <i className="ri-check-line text-2xl"></i>
                      </span>
                      <p className="mt-4 font-heading text-lg font-bold text-foreground-950">提交成功</p>
                      <p className="mt-2 text-sm text-foreground-600">感谢你的留言，我们会尽快通过邮件回复你。</p>
                      <button
                        type="button"
                        onClick={() => setStatus('idle')}
                        className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-label text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <i className="ri-edit-line"></i>
                        再写一条
                      </button>
                    </div>
                  ) : (
                    <form data-readdy-form id="about-contact-form" onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="contact-name" className="mb-1.5 block text-xs font-label text-foreground-600">
                            称呼
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            placeholder="请输入你的称呼"
                            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="mb-1.5 block text-xs font-label text-foreground-600">
                            邮箱
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            placeholder="请输入你的邮箱"
                            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-label text-foreground-600">
                          反馈类型
                        </label>
                        <select
                          id="contact-subject"
                          name="subject"
                          required
                          defaultValue=""
                          className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        >
                          <option value="" disabled>
                            请选择反馈类型
                          </option>
                          <option value="内容建议">内容建议</option>
                          <option value="纠错反馈">纠错反馈</option>
                          <option value="合作咨询">合作咨询</option>
                          <option value="其他问题">其他问题</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="mb-1.5 block text-xs font-label text-foreground-600">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          placeholder="请描述你的建议或问题（最多 500 字）"
                          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        />
                      </div>

                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="gs-field-alt"
                      />

                      {status === 'error' ? (
                        <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                          <i className="ri-error-warning-line"></i>
                          {formError}
                        </p>
                      ) : null}

                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950 sm:w-auto"
                      >
                        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                        {status === 'submitting' ? '提交中…' : '提交留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={140}>
          <section className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                <a href="#disclaimer">免责声明</a>
              </h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
                <p>本站为非官方演示站点，所有资讯、角色、活动与版本数据均为虚构示例内容，仅用于展示信息架构与交互效果。</p>
                <p>文中涉及的名称与数据不代表任何真实企业或产品的官方立场，如有雷同纯属巧合。</p>
                <p>本站不提供任何下载、支付或账号服务，页面内的按钮与链接仅用于功能演示。</p>
              </div>
            </div>
            <div className="rounded-xl border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                <a href="#privacy">隐私说明</a>
              </h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
                <p>本站不收集你的游戏账号信息，通过表单提交的联系方式仅用于回复你的留言。</p>
                <p>浏览偏好（例如收藏内容）仅保存在你的浏览器本地，用于提升浏览体验，不会上传到服务器。</p>
                <p>你可以随时清除浏览器本地数据来删除这些偏好记录。</p>
              </div>
            </div>
          </section>
        </Reveal>
      </div>
    </div>
  );
}